import { BfiApi, SearchResponse, IApiResponse, ApiResponse } from './bfiApi';
import { DrawBudgetLineCategory, DrawStatus, DrawType, LoanType } from '../../../constants/src';

export interface IDrawBudget {
    id: number;
    loanId: number;
    loanTypeId: number;
    loanPropertyId: number;
    businessName: string;
    propertyAddress: string;
    drawCount: number;
    createdDate: Date;
    equityRequirement: number;
    equityMetOn: Date;
    loanStatusId: number;
    bankAccountLast4Digits: string;
    suppressClientEmails: boolean;
    currentApproval?: IDrawBudgetApproval;
    drawPermissionsAssignedByUserId?: string;
    isCurrentUserDrawApprover: boolean;
    hasEntityUser: boolean;

    drawBudgetItems: IDrawBudgetItem[];
    draws: IDraw[];
    approvals: IDrawBudgetApproval[];

    isApproved: boolean;
    isFunded: boolean;
    isEquityMet: boolean;
    isOutOfBalance: boolean;
    isOutOfBalanceWaived: boolean;
    budgetTotal: boolean;
    drawTotal: boolean;
    isBudgetMaxed: boolean;
    canAddDraw: boolean;
    canGCAddDraw: boolean;
}

export interface IDrawBudgetItem {
    id: number;
    description: string;
    budgetAmount: number;
    equityAmount: number;
    acquisitionAmount: number;
    category: DrawBudgetLineCategory;
    hasReceipts: boolean;
    drawItems?: IDrawItem[];
}

export interface IDrawItem {
    id: number;
    drawId: number;
    drawNumber: number;

    funded: number;
    inspection: number;
    borrowerRequest: number;

    notes: string;
}

export interface IDraw {
    id: number;
    drawNumber: number;
    notes: string;
    drawType: DrawType;
    inspectionId: number;
    drawBudgetId?: number;

    isFinalized: boolean;
    isInspectionRequireFirst: boolean;
    borrowerSubmitted: boolean;
    needsBorrowerSignature: boolean;
    hasResubmitted: boolean;

    borrowerRequested: Date;
    inspectionOrdered: Date;
    inspectedOn: Date;
    fundedOn: Date;
    isOutOfBalance: boolean;
    isOutOfBalanceWaived: boolean;
    isLatestDraw: boolean;

    payeeFormDownloadToken: string;
    drawSignatureDownloadToken: string;

    // GC signature info for approval scenarios
    generalContractorSignatureId?: number;
    borrowerSignatureId?: number;
    generalContractorSignatureRequiresApproval?: boolean;
    hasGeneralContractorSignature?: boolean;
    hasBorrowerSignature?: boolean;

    drawItems?: IDrawItem[];
}

export interface IDrawBudgetApproval {
    approvedOn: Date;
    approvedBy: string;
}

export interface IDrawBudgetResponse extends Omit<IApiResponse, 'result'> {
    result: IDrawBudget;
}

export interface IAddDraw {
    drawBudgetId: number;
    drawType: DrawType;
    fontId?: number;
    ipAddress?: string;
}

export interface IDrawBudgetItemResponse extends Omit<IApiResponse, 'result'> {
    result: IDrawBudgetItem;
}

export interface IDrawBudgetApprovalResponse extends Omit<IApiResponse, 'result'> {
    result: IDrawBudgetApproval;
}

export interface IPostSaveDrawItem {
    drawBudgetItemId: number;
    drawId: number;
    drawNumber: number;
    value: number;
}

export interface IPostDownloadPayeeFormPdf {
    drawId: number;
    dateIssued: Date;
    bankName: string;
    drawFee: number;
}

export interface IPostFinalizeDraw {
    drawId: number;
    drawFee: number;
}

export interface IPostDrawBorrowerSignature {
    id?: number;
    drawId: number;
    fontId: number;
    typeName?: string;
    title?: string;
    ipAddress?: string;
    dateSigned?: Date;
}

export interface IPostDrawWaiverData {
    drawId: number;
    drawWaiverType: number;
    drawWaiverActionType: number;
    isOutOfBalanceWaived: boolean;
}

export interface IDrawReceipt {
    drawReceiptId: number;
    downloadToken?: string;
    drawBudgetItemId?: number;
    description?: string;
    amount?: number;
    note?: string;
    file?: File;
    fileName?: string;
}

export interface IDrawDashboardGrid {
    drawBugetId: number;
    entityName: string;
    bfiNumber: string;
    loanNumber: string;
    bfiNumberSuffix: number;
    isPortfolio: boolean;
    loanType: LoanType;
    drawType: DrawType;
    drawStatus: DrawStatus;
    borrowerRequest: number;
    funded: number;
    pointOfContact: string;
    submitted: Date;
    fundedOn: Date;
    inspectionOrdered: Date;
    notes: string;
}

export interface IFeedback {
    drawBudgetId: number | undefined;
    feedbackCategory: number;
    feedbackType: number;
    comment: string;
}

export interface IFeedbackGrid {
    feedbackCategory: number;
    feedbackType: number;
    comment: string;
    feedbackDate: any;
}

export interface IDashboardDrawBudget {
    loanId: number;
    drawBudgetId: number;
    address: string;
    committed: number;
    funded: number;
    available: number;
    bankAccountLast4Digits: number;
    closingDate: Date;
    latestDrawType: DrawType;
    borrowerRequestTotal: number;
    percentFunded: number;

    isApproved: boolean;
    isFunded: boolean;
    isBudgetMaxed: boolean;
    canAddDraw: boolean;
    isOutOfBalance: boolean;
    isOutOfBalanceWaived: boolean;
    isEquityMet: boolean;
    borrowerSubmitted: boolean;
    needsBorrowerSignature: boolean;

    isCurrentUserDrawApprover: boolean;
    borrowerSignatureId?: number;
    generalContractorSignatureId?: number;
    generalContractorSignatureRequiresApproval?: boolean;

    currentApproval: {
        approvedOn: Date;
        approvedBy: string;
    };

    drawHistoryLines: IDrawHistory[];
    canGCAddDraw: boolean;
}

export interface IDrawHistory {
    drawNumber: number;
    borrowerRequested: Date;
    inspectionOrdered: Date;
    inspectedOn: Date;
    fundedOn: Date;
    amountFunded: number;
    amountInspected: number;
    amountRequested: number;
    isBalanceFlagged: boolean;
    drawType: string;
    notes: string;
}

export interface IGCUserEntity {
    authorizedDrawUserId?: number;
    ownerUserId: string;
    borrowerEntityId?: number;
    name: string;
    userRole: string;
    gcAcceptedUtc?: string;
}

export interface IGCUserEntitiesResponse extends IApiResponse {
    drawEntities: IGCUserEntity[];
    ownedEntity?: IGCUserEntity | null;
}

export interface IGCDrawsByBorrowerCriteria {
    borrowerEntityId: number;
    assigningUserId: string;
}

export interface IUpdateDrawAuthorizedUserDto {
    id: number;
    gcAcceptedUtc?: string;
}

export class DrawApi extends BfiApi {
    getDrawBudget(id: number): Promise<IDrawBudgetResponse> {
        return super.get(`/draw/getDrawBudget/${id}`);
    }

    getDrawTable(id: number): Promise<ApiResponse<IDrawBudgetItem[]>> {
        return super.get(`/draw/getDrawTable/${id}`);
    }

    drawBudgetEquityRequirement(drawBudgetId: number, equityRequirement: number): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetEquityRequirement/${drawBudgetId}`, `"${equityRequirement}"`);
    }

    drawBudgetEquityMetOn(drawBudgetId: number, equityMetOn: Date): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetEquityMetOn/${drawBudgetId}`, `"${this.formatDateAsString(equityMetOn)}"`);
    }

    drawBudgetBankAccountLast4DigitsOn(drawBudgetId: number, bankAccountLast4Digits: string): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetBankAccountLast4DigitsOn/${drawBudgetId}`, `"${bankAccountLast4Digits}"`);
    }

    drawBudgetSuppressClientEmails(drawBudgetId: number, suppressClientEmails: boolean): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetSuppressClientEmails/${drawBudgetId}`, `${suppressClientEmails}`);
    }

    drawBudgetItemDescription(drawBudgetItemId: number, description: string): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetItemDescription/${drawBudgetItemId}`, `"${description}"`);
    }

    drawBudgetItemBudgetAmount(drawBudgetItemId: number, budgetAmount: number): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetItemBudgetAmount/${drawBudgetItemId}`, `"${budgetAmount}"`);
    }

    drawBudgetItemEquityAmount(drawBudgetItemId: number, equityAmount: number): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetItemEquityAmount/${drawBudgetItemId}`, `"${equityAmount}"`);
    }

    drawBudgetItemAcquisitionAmount(drawBudgetItemId: number, acquisitionAmount: number): Promise<IApiResponse> {
        return super.post(`/draw/drawBudgetItemAcquisitionAmount/${drawBudgetItemId}`, `"${acquisitionAmount}"`);
    }

    drawBorrowerRequested(drawId: number, borrowerRequested: Date): Promise<IApiResponse> {
        if (borrowerRequested) {
            var formattedValue = this.formatDateAsString(borrowerRequested);

            if (formattedValue) return super.post(`/draw/drawBorrowerRequested/${drawId}`, `"${formattedValue}"`);
        }
    }

    drawFundedOn(drawId: number, fundedOn: Date): Promise<IApiResponse> {
        if (fundedOn) {
            var formattedValue = this.formatDateAsString(fundedOn);

            if (formattedValue) return super.post(`/draw/drawFundedOn/${drawId}`, `"${formattedValue}"`);
        } else {
            return super.post(`/draw/drawFundedOn/${drawId}`, null);
        }
    }

    drawInspectionOrdered(drawId: number, inspectionOrdered: Date): Promise<IApiResponse> {
        if (inspectionOrdered) {
            var formattedValue = this.formatDateAsString(inspectionOrdered);

            if (formattedValue) return super.post(`/draw/drawInspectionOrdered/${drawId}`, `"${formattedValue}"`);
        } else {
            return super.post(`/draw/drawInspectionOrdered/${drawId}`, null);
        }
    }

    drawInspectedOn(drawId: number, inspectedOn: Date): Promise<IApiResponse> {
        if (inspectedOn) {
            var formattedValue = this.formatDateAsString(inspectedOn);

            if (formattedValue) return super.post(`/draw/drawInspectedOn/${drawId}`, `"${formattedValue}"`);
        } else {
            return super.post(`/draw/drawInspectedOn/${drawId}`, null);
        }
    }

    drawNotes(drawId: number, notes: string): Promise<IApiResponse> {
        return super.post(`/draw/DrawNotes/${drawId}`, `${notes}`);
    }

    drawItemInspection(data: IPostSaveDrawItem): Promise<IApiResponse> {
        return super.post('/draw/drawItemInspection', data);
    }

    drawItemFunded(data: IPostSaveDrawItem): Promise<IApiResponse> {
        return super.post('/draw/drawItemFunded', data);
    }

    drawItemNotes(data: IPostSaveDrawItem): Promise<IApiResponse> {
        return super.post('/draw/drawItemNotes', data);
    }

    drawItemBorrowerRequest(data: IPostSaveDrawItem): Promise<IDrawBudgetResponse> {
        return super.post('/draw/drawItemBorrowerRequest', data);
    }

    drawItemBorrowerRequest_v2_mobile(data: IPostSaveDrawItem[]): Promise<IDrawBudgetResponse> {
        return super.post('/draw/drawItemBorrowerRequest_v2_mobile', data);
    }

    copyInspectionToFunded(drawId: number): Promise<IDrawBudgetResponse> {
        return super.post(`/draw/copyInspectionToFunded/${drawId}`);
    }

    copyBorrowerRequestToFunded(drawId: number): Promise<IDrawBudgetResponse> {
        return super.post(`/draw/copyBorrowerRequestToFunded/${drawId}`);
    }

    addDraw(data: IAddDraw): Promise<IApiResponse> {
        return super.post(`/draw/addDraw/${data.drawBudgetId}`, {
            drawType: data.drawType,
            fontId: data.fontId,
            ipAddress: data.ipAddress,
        });
    }

    deleteDraw(drawId: number): Promise<IDrawBudgetResponse> {
        return super.post(`/draw/deleteDraw/${drawId}`);
    }

    addDrawBudget(propertyId: number, isPopulated: boolean): Promise<IDrawBudgetResponse> {
        return super.post(`/draw/addDrawBudget/${propertyId}`, `${isPopulated}`);
    }

    addDrawBudgetItem(drawBudgetId: number, category: DrawBudgetLineCategory): Promise<IDrawBudgetItemResponse> {
        return super.post(`/draw/addDrawBudgetItem/${drawBudgetId}`, category);
    }

    deleteDrawBudgetItem(drawBudgetItemId: number): Promise<void> {
        return super.post(`/draw/deleteDrawBudgetItem/${drawBudgetItemId}`);
    }

    finalizeDraw(data: IPostFinalizeDraw): Promise<any> {
        return super.post(`/draw/finalizeDraw/${data.drawId}`, {
            drawFee: data.drawFee,
        });
    }

    addApproval(drawBudgetId: number): Promise<IDrawBudgetApprovalResponse> {
        return super.post(`/draw/addApproval/${drawBudgetId}`);
    }

    clearApproval(drawBudgetId: number): Promise<IApiResponse> {
        return super.post(`/draw/clearApproval/${drawBudgetId}`);
    }

    emailBudgetPdf(drawBudgetId: number): Promise<any> {
        return super.get(`/draw/emailBudgetPdf/${drawBudgetId}`, {
            responseType: 'blob',
        });
    }

    emailPayeeFormPdf(data: IPostDownloadPayeeFormPdf): Promise<any> {
        return super.post(
            `/draw/emailPayeeFormPdf/${data.drawId}`,
            {
                dateIssued: data.dateIssued,
                bankName: data.bankName,
                drawFee: data.drawFee,
            },
            { responseType: 'blob' }
        );
    }

    setDrawWaiver(data: IPostDrawWaiverData): Promise<IApiResponse> {
        return super.post('/draw/setDrawWaiver', data);
    }

    drawBorrowerSignature(data: IPostDrawBorrowerSignature): Promise<any> {
        return super.post('/draw/drawBorrowerSignature', data);
    }

    signGCRequestedDraw(data: IPostDrawBorrowerSignature): Promise<any> {
        return super.post(`/draw/signGCRequestedDraw/${data.drawId}`, { fontId: data.fontId, ipAddress: data.ipAddress });
    }

    resignBorrowerSubmission(drawId: number): Promise<any> {
        return super.post(`/draw/resignBorrowerSubmission/${drawId}`);
    }

    getDrawReceipts(drawId: string): Promise<ApiResponse<IDrawReceipt[]>> {
        return super.get(`/draw/drawReceipts/${drawId}`);
    }

    addOrUpdateDrawReceipt(data: IDrawReceipt): Promise<any> {
        const formData = new FormData();

        if (data.drawReceiptId) formData.append('drawReceiptId', data.drawReceiptId.toString());
        if (data.drawBudgetItemId) formData.append('drawBudgetItemId', data.drawBudgetItemId.toString());
        if (data.amount) formData.append('amount', data.amount.toString());
        if (data.note) formData.append('note', data.note);
        if (data.file) formData.append('file', data.file);

        return super.post('/draw/drawReceipt', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    deleteReceipt(drawReceiptId: number): Promise<any> {
        return super.post(`/draw/deleteReceipt/${drawReceiptId}`);
    }

    addFeedback(data: IFeedback): Promise<IApiResponse> {
        return super.post('/feedback/addFeedback', data);
    }

    getAllFeedback(): Promise<any> {
        return super.get(`/feedback/getAllFeedback`);
    }

    dashboard({
        startDate,
        endDate,
        drawStatus,
    }: {
        startDate: Date;
        endDate: Date;
        drawStatus: DrawStatus | undefined;
    }): Promise<SearchResponse<IDrawDashboardGrid[]>> {
        return super.post('/draw/dashboard', { startDate, endDate, drawStatus });
    }

    dashboardDrawBudgets(): Promise<ApiResponse<IDashboardDrawBudget[]>> {
        return super.get('/draw/outstandingDrawGrid');
    }

    dashboardDrawBudgetsGC(criteria: IGCDrawsByBorrowerCriteria): Promise<ApiResponse<IDashboardDrawBudget[]>> {
        return super.post(`/draw/outstandingDrawGridGC`, criteria);
    }

    getGCEntities(): Promise<ApiResponse<IGCUserEntitiesResponse>> {
        return super.get('/draw/getGeneralContractorEntities');
    }

    updateDrawAuthorizedUser(dto: IUpdateDrawAuthorizedUserDto): Promise<IApiResponse> {
        return super.put('/draw/updateDrawAuthorizedUser', dto);
    }

    acceptGCAuthorization(authorizedDrawUserId: number): Promise<IApiResponse> {
        return super.post(`/draw/acceptGCAuthorization/${authorizedDrawUserId}`, {});
    }
}
